/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SystemGeneralTabVoicemail.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the System General module.
----------------------------------------------------------------*/


import type { ReactNode } from 'react';
import { Voicemail } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { useI18n } from '../../../hooks/useI18n';
import { SystemGeneralSettings } from '../types/systemGeneral.types';

interface Props {
    formData: SystemGeneralSettings;
    renderField: (field: keyof SystemGeneralSettings, control: ReactNode) => ReactNode;
    onChange: (field: keyof SystemGeneralSettings, value: SystemGeneralSettings[keyof SystemGeneralSettings]) => void;
}

export function SystemGeneralTabVoicemail({ formData, renderField, onChange }: Props) {
    const { t } = useI18n();
    const vmDisabled = !formData.voicemailEnabled;

    return (
        <FormBox title={t('systemGeneral.tabs.voicemail')} icon={<Voicemail className="h-5 w-5" />}>
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    {/* Voicemail */}
                    {renderField('voicemailEnabled', <Toggle value={formData.voicemailEnabled} onChange={(v) => onChange('voicemailEnabled', v)} />)}
                    {renderField('voicemailMaxMessages', <Input type="number" min={10} max={1000} value={formData.voicemailMaxMessages} onChange={(e) => onChange('voicemailMaxMessages', parseInt(e.target.value, 10))} disabled={vmDisabled} />)}
                    {renderField('voicemailNotificationEnabled', <Toggle value={formData.voicemailNotificationEnabled} onChange={(v) => onChange('voicemailNotificationEnabled', v)} disabled={vmDisabled} />)}
                    {renderField('voicemailTranscribeEnabled', <Toggle value={formData.voicemailTranscribeEnabled} onChange={(v) => onChange('voicemailTranscribeEnabled', v)} disabled={vmDisabled} />)}
                    {renderField('voicemailEmailAttachment', <Toggle value={formData.voicemailEmailAttachment} onChange={(v) => onChange('voicemailEmailAttachment', v)} disabled={vmDisabled} />)}

                    <div className="col-span-full border-t border-border my-2" />

                    {/* Call Recording */}
                    {renderField('recordIncoming', <Toggle value={formData.recordIncoming} onChange={(v) => onChange('recordIncoming', v)} />)}
                    {renderField('recordOutgoing', <Toggle value={formData.recordOutgoing} onChange={(v) => onChange('recordOutgoing', v)} />)}
                    {renderField('recordInternal', <Toggle value={formData.recordInternal} onChange={(v) => onChange('recordInternal', v)} />)}
                    {renderField('recordOnDemand', <Toggle value={formData.recordOnDemand} onChange={(v) => onChange('recordOnDemand', v)} />)}

                    {renderField('recordingFormat',
                        <select value={formData.recordingFormat} onChange={(e) => onChange('recordingFormat', e.target.value)} className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground w-full">
                            <option value="wav">{t('systemGeneral.options.recordingFormat.wav', 'WAV')}</option>
                            <option value="mp3">{t('systemGeneral.options.recordingFormat.mp3', 'MP3')}</option>
                            <option value="gsm">{t('systemGeneral.options.recordingFormat.gsm', 'GSM')}</option>
                        </select>
                    )}
                </div>
            </div>
        </FormBox>
    );
}
